import CloseIcon from 'assets/icons/close-icon.svg?component'
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock'
import { AnimatePresence, motion } from 'framer-motion'
import { useDeviceInfo } from 'hooks/useDeviceInfo'
import { MouseEvent, ReactNode, useEffect } from 'react'
import tw, { TwStyle } from 'twin.macro'

import Button from '../Buttons/Button'
import Typography from '../Typography'
import Heading from '../Typography/Heading'

type ModalProps = {
  open: boolean
  children: ReactNode
  title?: ReactNode
  outsideClose?: boolean
  twStyle?: TwStyle
  headerStyle?: TwStyle
  wrapperStyle?: TwStyle
  backdropTwStyle?: TwStyle
  onClose?: () => void
  withBackdrop?: boolean
  divider?: boolean
}

const Modal = ({
  open = false,
  children,
  title,
  withBackdrop = true,
  twStyle,
  divider = false,
  headerStyle,
  wrapperStyle,
  onClose,
  outsideClose = true
}: ModalProps) => {
  const { isDesktop } = useDeviceInfo()

  const handleClose = () => {
    onClose?.()
  }

  const handleOutside = (event: MouseEvent) => {
    if (outsideClose) {
      event.preventDefault()
      onClose?.()
    }
  }

  useEffect(() => {
    if (open) {
      isDesktop && disableBodyScroll(document.documentElement)
    }
    return () => {
      isDesktop && enableBodyScroll(document.documentElement)
    }
  }, [isDesktop, open])

  return (
    <AnimatePresence>
      {open && (isDesktop || !withBackdrop) && (
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions
        <div
          css={[
            tw`min-h-screen overflow-y-scroll bg-black-20 flex fixed top-0 left-0 bottom-0 right-0 z-50 sm:p-5 !mt-[-5px]`,
            wrapperStyle
          ]}
          onClick={handleOutside}
        >
          <motion.div
            css={[tw`p-8 m-auto bg-secondary rounded-2xl flex flex-col`, twStyle]}
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 50 }}
            transition={{ duration: '0.5' }}
            onClick={e => {
              e.stopPropagation()
              e.preventDefault()
            }}
          >
            <div tw='flex items-center gap-x-6 justify-between' css={[headerStyle]}>
              <Heading variant='title2' weight='bold'>
                {title}
              </Heading>
              <div tw='self-center'>
                <CloseIcon onClick={handleClose} tw='cursor-pointer flex-shrink-0 w-4 h-4' />
              </div>
            </div>
            {divider && <div tw='h-[10px] w-full bg-main'></div>}
            {children}
          </motion.div>
        </div>
      )}

      {open && !isDesktop && withBackdrop && (
        <div tw='fixed bottom-0 left-0 right-0 overflow-auto w-screen z-50 rounded-t-2xl bg-black-20'>
          <motion.div
            initial={{ height: 0, position: 'relative' }}
            animate={{ height: 'auto' }}
            exit={{ height: 0 }}
            transition={{ duration: 0.5 }}
            tw='overflow-hidden'
          >
            <div tw='bg-secondary rounded-t-2xl p-5 w-full h-full'>
              <div tw='flex justify-between items-center' css={[headerStyle]}>
                <Typography variant='body' weight='bold'>
                  {title}
                </Typography>
                <Button variant='link' onClick={handleClose}>
                  <span tw='inline-block text-sm leading-5 font-bold text-primary-middle'>Закрыть</span>
                </Button>
              </div>
              {divider && <div tw='h-[12px] w-full bg-main'></div>}
              <div>{children}</div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  )
}

export { Modal }
